import React, { useState } from "react";
import PropTypes from "prop-types";
import { uniqueId } from "lodash";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { Translate } from "react-localize-redux";
import Editor from "./Editor";

const Input = ({
  title,
  headTitle,
  className,
  type,
  placeholder,
  disabled,
  required,
  defaultValue,
  onChange,
  inputProps,
  value,
  name,
  iconNode,
  onIconClick,
  file,
  error,
  fileType,
  decimals,
  maxLength,
  isEditor,
  props,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [id] = useState(uniqueId("input-"));

  return (
    <div
      className="input-group d-flex flex-column justify-content-center mb-0 mw-100 position-relative"
      data-has-error={Boolean(error)}
      data-has-tooltip={Boolean(disabled)}
    >
      {title && (
        <label className="form-label fw-bold pb-0" htmlFor={id}>
          {title}
        </label>
      )}
      <fieldset
        className={`fieldset d-flex align-items-start bg-white position-relative mw-100 ${
          headTitle ? "flex-column" : ""
        } ${isActive ? "input-active" : ""} ${
          (isEditor && disabled && value) || (isEditor && !disabled)
            ? `border-0`
            : ``
        }`}
      >
        {headTitle && (
          <p className="headTitle text-secondary fw-bold mb-0 ms-2 px-1">
            {headTitle}
          </p>
        )}
        {isEditor ? (
          <Editor
            value={defaultValue || value}
            onChange={(value) =>
              onChange({
                target: {
                  value,
                  name,
                },
              })
            }
            placeholder={placeholder}
            disabled={disabled}
            className={`input form-control ${className ? className : ""}`}
            {...inputProps}
          />
        ) : (
          <input
            type={
              type === "password" && showPassword
                ? `text`
                : type === "number"
                ? "text"
                : type
            }
            className={`input form-control ${className ? className : ""} ${
              type === "file" ? "d-none" : ""
            }`}
            id={id}
            name={name}
            value={type !== "file" ? defaultValue || value : ""}
            required={required}
            disabled={disabled}
            maxLength={maxLength}
            onClick={() => (type !== "file" ? setIsActive(true) : null)}
            onBlur={() => {
              setIsActive(false);

              if (decimals === undefined) return;

              const currentValue = defaultValue || value;
              if (currentValue === undefined || currentValue === null || currentValue === "") return;

              const parsed = parseFloat(currentValue);
              if (Number.isNaN(parsed)) return;

              const formatted = decimals === 0 ? String(Math.trunc(parsed)) : parsed.toFixed(decimals);
              if (formatted !== String(currentValue)) {
                onChange({ target: { value: formatted } });
              }
            }}
            onKeyDown={
              type === "number"
                ? (e) => {
                    const allowedKeys = [
                      "Backspace", "Delete", "Tab", "Escape", "Enter",
                      "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
                      "Home", "End",
                    ];
                    if (allowedKeys.includes(e.key)) return;
                    if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key)) return;
                    if (e.key === "." && !e.target.value.includes(".")) return;
                    if (!/^\d$/.test(e.key)) e.preventDefault();
                  }
                : undefined
            }
            onChange={
              decimals !== undefined
                ? (e) => {
                    let val = e.target.value;

                    val = val.replace(/[^0-9.]/g, "");

                    if (decimals === 0) {
                      val = val.replace(".", "");
                    } else if (val.includes(".")) {
                      const valSplit = val.split(".");

                      if (valSplit.length > 2) {
                        // remove all dots except the last one
                        val = valSplit
                          .slice(0, valSplit.length - 1)
                          .join("")
                          .concat(`.${valSplit[valSplit.length - 1]}`);
                      }

                      const afterDot = val.split(".")[1];

                      if (afterDot && afterDot.length > decimals) {
                        val = val.slice(0, val.length - 1);
                      }
                    }

                    onChange({
                      target: {
                        value: val,
                      },
                    });
                  }
                : (e) => {
                    e.preventDefault();
                    if (type === "email") {
                      e.target.value = e.target.value.toLowerCase();

                      if (/[^0-9a-zA-Z.\-_@]/g.test(e.target.value)) return;
                      if (e.target.value.includes("..")) return;
                      if (e.target.value.split("@").length > 2) return;

                      onChange(e);
                    } else onChange(e);
                  }
            }
            placeholder={placeholder}
            {...inputProps}
          />
        )}
        {type === "file" && fileType === "image" && file ? (
          <div className="file-input d-flex align-items-center justify-content-center p-2 w-100">
            <img
              src={file?.base64 || file?.file_path}
              alt="file"
              className="img-fluid mw-100 mh-100"
            />
          </div>
        ) : null}

        {type === "file" && !file ? (
          <label
            className="input-file-label w-100 d-flex align-items-center justify-content-start"
            htmlFor={id}
          >
            {placeholder}
          </label>
        ) : (
          ""
        )}
        {type === "file" && file ? (
          <div className="d-flex flex-column justify-content-center p-2 w-100">
            <label
              htmlFor={id}
              className="file-name m-0 d-inline-block text-center text-truncate mw-100"
            >
              {file.name}
            </label>
            {fileType === "image" && (
              <label
                htmlFor={id}
                className="input-file-label text-orange d-flex align-items-center justify-content-center"
              >
                <Translate id="change_image" />
              </label>
            )}
            {fileType === "file" && (
              <label
                htmlFor={id}
                className="input-file-label text-orange d-flex align-items-center justify-content-center"
              >
                <Translate id="change_file" />
              </label>
            )}
          </div>
        ) : null}
        {iconNode && (
          <label className="form-label" htmlFor={id} onClick={onIconClick}>
            {iconNode}
          </label>
        )}
        {type === "password" ? (
          <label
            className="show-password-label position-absolute"
            htmlFor={id}
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <AiFillEyeInvisible className="fs-5" />
            ) : (
              <AiFillEye className="fs-5" />
            )}
          </label>
        ) : (
          ``
        )}
      </fieldset>
      {error && <p className="error-text">{error}</p>}
      {disabled && !isEditor && (
        <p className="input-tooltip position-absolute m-0">{value}</p>
      )}
    </div>
  );
};

Input.propTypes = {
  title: PropTypes.node,
  className: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
  onChange: PropTypes.func,
  iconNode: PropTypes.node,
  inputProps: PropTypes.object,
  file: PropTypes.object,
  props: PropTypes.object,
  error: PropTypes.string,
};

export default Input;
